import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Dropdown,
  Grid,
  Input,
  InputNumber,
  Modal,
  Progress,
  Row,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  DownloadOutlined,
  PrinterOutlined,
  SaveOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { openDailyLogSheetPrint } from '@/components/pmv/PrintablePmvCommercial';
import pmvFieldOpsService, {
  type PmvDailyLogSheetEntry,
  type PmvSheetValidateResult,
} from '@/services/pmv-field-ops.service';
import { useProjects } from '@/hooks/useProjects';
import { buildProjectIdToLabelMap, resolveProjectDisplayFromMap } from '@/utils/pmvDisplay';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

type SheetRow = PmvDailyLogSheetEntry & {
  _dirty?: boolean;
  _localKey: string;
  fuel_litres?: number | null;
  issue_flag?: string | null;
  ownership_hint?: string | null;
  hire_flag?: boolean | null;
};

type ColGroup =
  | 'IDENTITY'
  | 'OPERATION'
  | 'TIME'
  | 'METER'
  | 'OPERATOR'
  | 'FUEL'
  | 'ISSUES'
  | 'COMMERCIAL'
  | 'AUDIT';

type ColDef = {
  key: string;
  label: string;
  group: ColGroup;
  width: number;
  editable?: boolean;
  type?: 'text' | 'number' | 'bool';
};

const ALL_COLS: ColDef[] = [
  { key: 'line_no', label: '#', group: 'IDENTITY', width: 48 },
  { key: 'equipment_code', label: 'Equip', group: 'IDENTITY', width: 110, editable: true, type: 'text' },
  { key: 'expected_flag', label: 'Exp', group: 'IDENTITY', width: 52, editable: true, type: 'bool' },
  { key: 'unexpected_reason', label: 'Unexpected', group: 'IDENTITY', width: 110, editable: true, type: 'text' },
  { key: 'work_description', label: 'Work', group: 'OPERATION', width: 160, editable: true, type: 'text' },
  { key: 'shift_code', label: 'Shift', group: 'OPERATION', width: 72, editable: true, type: 'text' },
  { key: 'sequence_no', label: 'Seq', group: 'OPERATION', width: 56, editable: true, type: 'number' },
  { key: 'working_minutes', label: 'Work m', group: 'TIME', width: 72, editable: true, type: 'number' },
  { key: 'idle_minutes', label: 'Idle m', group: 'TIME', width: 72, editable: true, type: 'number' },
  { key: 'standby_minutes', label: 'Stby m', group: 'TIME', width: 72, editable: true, type: 'number' },
  { key: 'breakdown_minutes', label: 'BD m', group: 'TIME', width: 72, editable: true, type: 'number' },
  { key: 'other_non_operating_minutes', label: 'Other m', group: 'TIME', width: 72, editable: true, type: 'number' },
  { key: 'opening_meter_value', label: 'Open mtr', group: 'METER', width: 88, editable: true, type: 'number' },
  { key: 'closing_meter_value', label: 'Close mtr', group: 'METER', width: 88, editable: true, type: 'number' },
  { key: 'operator_employee_id', label: 'Operator', group: 'OPERATOR', width: 120, editable: true, type: 'text' },
  { key: 'fuel_litres', label: 'Fuel L', group: 'FUEL', width: 72, editable: true, type: 'number' },
  { key: 'issue_flag', label: 'Issue', group: 'ISSUES', width: 100, editable: true, type: 'text' },
  { key: 'remarks', label: 'Remarks', group: 'ISSUES', width: 140, editable: true, type: 'text' },
  { key: 'ownership_hint', label: 'Own', group: 'COMMERCIAL', width: 90, editable: true, type: 'text' },
  { key: 'hire_flag', label: 'Hired', group: 'COMMERCIAL', width: 60, editable: true, type: 'bool' },
  { key: 'daily_log_id', label: 'Log', group: 'AUDIT', width: 90 },
  { key: 'last_row_result', label: 'Result', group: 'AUDIT', width: 110 },
  { key: 'last_row_message', label: 'Msg', group: 'AUDIT', width: 140 },
];

const GROUP_ORDER: ColGroup[] = [
  'IDENTITY',
  'OPERATION',
  'TIME',
  'METER',
  'OPERATOR',
  'FUEL',
  'ISSUES',
  'COMMERCIAL',
  'AUDIT',
];

const EDITABLE = new Set(['DRAFT', 'IN_PROGRESS', 'RETURNED']);

function entryToRow(e: PmvDailyLogSheetEntry): SheetRow {
  const payload = e.row_payload_json || {};
  return {
    ...e,
    _localKey: e.id,
    _dirty: false,
    fuel_litres: (payload.fuel_litres as number | null | undefined) ?? null,
    issue_flag: (payload.issue_flag as string | null | undefined) ?? null,
    ownership_hint: (payload.ownership_hint as string | null | undefined) ?? null,
    hire_flag: (payload.hire_flag as boolean | null | undefined) ?? null,
  };
}

function rowToBulkPayload(r: SheetRow): Record<string, unknown> {
  return {
    id: r.id?.startsWith('local-') ? undefined : r.id,
    line_no: r.line_no,
    pmv_profile_id: r.pmv_profile_id,
    allocation_id: r.allocation_id,
    equipment_code: r.equipment_code,
    expected_flag: r.expected_flag,
    unexpected_reason: r.unexpected_reason,
    shift_code: r.shift_code,
    sequence_no: r.sequence_no,
    operator_employee_id: r.operator_employee_id,
    work_front_id: r.work_front_id,
    working_minutes: r.working_minutes,
    idle_minutes: r.idle_minutes,
    standby_minutes: r.standby_minutes,
    breakdown_minutes: r.breakdown_minutes,
    other_non_operating_minutes: r.other_non_operating_minutes,
    opening_meter_value: r.opening_meter_value,
    closing_meter_value: r.closing_meter_value,
    work_description: r.work_description,
    remarks: r.remarks,
    row_payload_json: {
      ...(r.row_payload_json || {}),
      fuel_litres: r.fuel_litres ?? null,
      issue_flag: r.issue_flag ?? null,
      ownership_hint: r.ownership_hint ?? null,
      hire_flag: r.hire_flag ?? null,
    },
  };
}

function validateRowLocal(r: SheetRow): string | null {
  if (!r.pmv_profile_id && !r.equipment_code) return 'Equipment required';
  if (
    r.opening_meter_value != null &&
    r.closing_meter_value != null &&
    Number(r.closing_meter_value) < Number(r.opening_meter_value)
  ) {
    return 'Closing meter < opening';
  }
  return null;
}

const cellStyle = (dirty?: boolean, err?: boolean): React.CSSProperties => ({
  borderRight: '1px solid #e8e8e8',
  borderBottom: '1px solid #e8e8e8',
  padding: '2px 4px',
  background: err ? '#fff2f0' : dirty ? '#fffbe6' : undefined,
  fontSize: 12,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
});

const PmvDailyLogSheetPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const screens = useBreakpoint();
  const isPhone = !screens.md;
  const { data: projects = [] } = useProjects();
  const projectLabels = useMemo(() => buildProjectIdToLabelMap(projects), [projects]);

  const [rows, setRows] = useState<SheetRow[]>([]);
  const [rowVersion, setRowVersion] = useState(1);
  const [visibleGroups, setVisibleGroups] = useState<Record<ColGroup, boolean>>(() =>
    Object.fromEntries(GROUP_ORDER.map((g) => [g, true])) as Record<ColGroup, boolean>
  );
  const [focus, setFocus] = useState<{ r: number; c: string } | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkField, setBulkField] = useState('working_minutes');
  const [bulkValue, setBulkValue] = useState<string | number>('');
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [validation, setValidation] = useState<PmvSheetValidateResult | null>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const sheetQ = useQuery({
    queryKey: ['pmv-daily-log-sheet', id],
    queryFn: () => pmvFieldOpsService.getSheet(id),
    enabled: !!id,
  });

  useEffect(() => {
    if (!sheetQ.data) return;
    setRowVersion(sheetQ.data.row_version);
    setRows((sheetQ.data.entries || []).map(entryToRow));
    setValidation(null);
  }, [sheetQ.data]);

  const sheet = sheetQ.data;
  const canEdit = sheet ? EDITABLE.has(String(sheet.status)) : false;

  const visibleCols = useMemo(
    () => ALL_COLS.filter((c) => visibleGroups[c.group]),
    [visibleGroups]
  );
  const totalWidth = useMemo(() => visibleCols.reduce((s, c) => s + c.width, 0), [visibleCols]);

  const dirtyCount = rows.filter((r) => r._dirty).length;
  const exceptionCount = useMemo(
    () =>
      rows.filter(
        (r) =>
          validateRowLocal(r) ||
          r.last_row_result === 'FAILED_VALIDATION' ||
          r.last_row_result === 'CONFLICT' ||
          !r.expected_flag
      ).length,
    [rows]
  );
  const hiredCount = rows.filter((r) => r.hire_flag).length;
  const completePct =
    rows.length === 0
      ? 0
      : Math.round(
          (rows.filter((r) => r.working_minutes > 0 || r.daily_log_id).length / rows.length) * 100
        );

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 32,
    overscan: 16,
  });

  const patchCell = useCallback((rowIdx: number, key: string, value: unknown) => {
    setRows((prev) => {
      const next = [...prev];
      const row = { ...next[rowIdx], [key]: value, _dirty: true };
      next[rowIdx] = row;
      return next;
    });
  }, []);

  const saveMut = useMutation({
    mutationFn: async () => {
      const dirty = rows.filter((r) => r._dirty);
      if (!dirty.length) return null;
      const chunkSize = 500;
      let version = rowVersion;
      for (let i = 0; i < dirty.length; i += chunkSize) {
        const chunk = dirty.slice(i, i + chunkSize).map(rowToBulkPayload);
        const result = await pmvFieldOpsService.bulkSave(id, {
          row_version: version,
          rows: chunk,
        });
        version = result.row_version;
      }
      return version;
    },
    onSuccess: async (version) => {
      if (version == null) {
        message.info('No dirty rows');
        return;
      }
      message.success('Sheet saved');
      setRowVersion(version);
      await qc.invalidateQueries({ queryKey: ['pmv-daily-log-sheet', id] });
    },
    onError: (e: Error) => message.error(e.message || 'Save failed'),
  });

  const lifecycleMut = useMutation({
    mutationFn: async (action: 'submit' | 'verify' | 'close' | 'return' | 'validate' | 'load') => {
      if (action === 'validate') return pmvFieldOpsService.validateSheet(id);
      if (action === 'load') return pmvFieldOpsService.loadExpected(id);
      if (dirtyCount > 0) {
        throw new Error('Save dirty rows before lifecycle action');
      }
      if (action === 'submit') return pmvFieldOpsService.submitSheet(id, { row_version: rowVersion });
      if (action === 'verify') return pmvFieldOpsService.verifySheet(id, { row_version: rowVersion });
      if (action === 'close') return pmvFieldOpsService.closeSheet(id, { row_version: rowVersion });
      return pmvFieldOpsService.returnSheet(id, { row_version: rowVersion, reason: 'Returned from sheet UI' });
    },
    onSuccess: async (data, action) => {
      if (action === 'validate') {
        setValidation(data as PmvSheetValidateResult);
        message.info((data as PmvSheetValidateResult).ok ? 'Validation OK' : 'Validation issues found');
        return;
      }
      message.success(`${action} complete`);
      await qc.invalidateQueries({ queryKey: ['pmv-daily-log-sheet', id] });
    },
    onError: (e: Error) => message.error(e.message || 'Action failed'),
  });

  const copyPrevious = () => {
    if (rows.length < 2) {
      message.info('Need at least two rows');
      return;
    }
    setRows((prev) => {
      const next = [...prev];
      for (let i = 1; i < next.length; i++) {
        const src = next[i - 1];
        next[i] = {
          ...next[i],
          working_minutes: src.working_minutes,
          idle_minutes: src.idle_minutes,
          standby_minutes: src.standby_minutes,
          work_description: src.work_description,
          operator_employee_id: src.operator_employee_id,
          _dirty: true,
        };
      }
      return next;
    });
    message.success('Copied time/operator from previous row down');
  };

  const applyBulk = () => {
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        [bulkField]:
          ALL_COLS.find((c) => c.key === bulkField)?.type === 'number'
            ? Number(bulkValue)
            : bulkValue,
        _dirty: true,
      }))
    );
    setBulkOpen(false);
    message.success('Bulk set applied locally — Save to persist');
  };

  const parseTsv = (text: string) => {
    const lines = text.trim().split(/\r?\n/);
    if (!lines.length) return;
    const headers = lines[0].split('\t').map((h) => h.trim());
    const mapped = lines.slice(1).map((line, idx) => {
      const cells = line.split('\t');
      const obj: Record<string, string> = {};
      headers.forEach((h, i) => {
        obj[h] = cells[i] ?? '';
      });
      return obj;
    });
    setRows((prev) => {
      const next = [...prev];
      mapped.forEach((m, i) => {
        if (!next[i]) return;
        const patch: Partial<SheetRow> = { _dirty: true };
        if (m.equipment_code != null) patch.equipment_code = m.equipment_code || next[i].equipment_code;
        if (m.working_minutes != null && m.working_minutes !== '')
          patch.working_minutes = Number(m.working_minutes);
        if (m.idle_minutes != null && m.idle_minutes !== '') patch.idle_minutes = Number(m.idle_minutes);
        if (m.standby_minutes != null && m.standby_minutes !== '')
          patch.standby_minutes = Number(m.standby_minutes);
        if (m.breakdown_minutes != null && m.breakdown_minutes !== '')
          patch.breakdown_minutes = Number(m.breakdown_minutes);
        if (m.opening_meter_value != null && m.opening_meter_value !== '')
          patch.opening_meter_value = Number(m.opening_meter_value);
        if (m.closing_meter_value != null && m.closing_meter_value !== '')
          patch.closing_meter_value = Number(m.closing_meter_value);
        if (m.work_description != null) patch.work_description = m.work_description;
        if (m.remarks != null) patch.remarks = m.remarks;
        if (m.operator_employee_id != null) patch.operator_employee_id = m.operator_employee_id;
        next[i] = { ...next[i], ...patch };
      });
      return next;
    });
    message.success(`Pasted ${mapped.length} rows (local) — Save to persist`);
    setImportOpen(false);
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text/plain');
    if (!text || !text.includes('\t')) return;
    e.preventDefault();
    parseTsv(text.includes('\n') && text.split('\n')[0].includes('equipment') ? text : `equipment_code\tworking_minutes\n${text}`);
  };

  const exportTsv = () => {
    const headers = visibleCols.map((c) => c.key).join('\t');
    const body = rows
      .map((r) => visibleCols.map((c) => String((r as unknown as Record<string, unknown>)[c.key] ?? '')).join('\t'))
      .join('\n');
    const blob = new Blob([`${headers}\n${body}`], { type: 'text/tab-separated-values' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sheet?.sheet_number || 'sheet'}.tsv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const moveFocus = (rowIdx: number, colKey: string, dir: 'tab' | 'enter' | 'left' | 'right' | 'up' | 'down') => {
    const colIdx = visibleCols.findIndex((c) => c.key === colKey);
    let nr = rowIdx;
    let nc = colIdx;
    if (dir === 'tab' || dir === 'right') nc += 1;
    if (dir === 'left') nc -= 1;
    if (dir === 'enter' || dir === 'down') nr += 1;
    if (dir === 'up') nr -= 1;
    if (nc >= visibleCols.length) {
      nc = 0;
      nr += 1;
    }
    if (nc < 0) {
      nc = visibleCols.length - 1;
      nr -= 1;
    }
    if (nr < 0 || nr >= rows.length || nc < 0 || nc >= visibleCols.length) return;
    setFocus({ r: nr, c: visibleCols[nc].key });
  };

  const attentionItems = useMemo(() => {
    const items: Array<{ key: string; text: string; severity: 'error' | 'warning' }> = [];
    rows.forEach((r, i) => {
      const err = validateRowLocal(r);
      if (err) items.push({ key: `${r._localKey}-v`, text: `L${r.line_no || i + 1}: ${err}`, severity: 'error' });
      if (r.last_row_result === 'FAILED_VALIDATION' || r.last_row_result === 'CONFLICT') {
        items.push({
          key: `${r._localKey}-r`,
          text: `L${r.line_no}: ${r.last_row_message || r.last_row_result}`,
          severity: 'error',
        });
      }
      if (!r.expected_flag) {
        items.push({
          key: `${r._localKey}-u`,
          text: `L${r.line_no}: unexpected unit`,
          severity: 'warning',
        });
      }
    });
    (validation?.issues || []).forEach((iss, i) => {
      items.push({
        key: `val-${i}`,
        text: `L${iss.line_no ?? '?'}: ${iss.message}`,
        severity: iss.severity === 'warning' ? 'warning' : 'error',
      });
    });
    return items;
  }, [rows, validation]);

  if (isPhone) {
    return (
      <WorkspaceLayout>
        <EnterprisePageHeader documentLabel="Daily Log Sheet" documentNumber={sheet?.sheet_number} />
        <Alert
          type="info"
          showIcon
          message="Bulk sheet editing is desktop/tablet only"
          description={
            <span>
              Open an individual daily log instead.{' '}
              <Link to="/asset/pmv/daily-logs">Go to Daily Logs</Link>
            </span>
          }
        />
      </WorkspaceLayout>
    );
  }

  const toolbar = (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        background: '#fafafa',
        borderBottom: '1px solid #d9d9d9',
        padding: '8px 12px',
        marginBottom: 8,
      }}
    >
      <Space wrap size={6}>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          disabled={!canEdit || dirtyCount === 0}
          loading={saveMut.isPending}
          onClick={() => saveMut.mutate()}
        >
          Save{dirtyCount ? ` (${dirtyCount})` : ''}
        </Button>
        <Button onClick={() => lifecycleMut.mutate('validate')} loading={lifecycleMut.isPending}>
          Validate
        </Button>
        <Button disabled={!canEdit} onClick={() => setBulkOpen(true)}>
          Bulk Set
        </Button>
        <Button disabled={!canEdit} icon={<CopyOutlined />} onClick={copyPrevious}>
          Copy Previous
        </Button>
        <Button disabled={!canEdit} icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
          Import
        </Button>
        <Button icon={<DownloadOutlined />} onClick={exportTsv}>
          Export
        </Button>
        <Button
          icon={<CheckOutlined />}
          disabled={!canEdit || dirtyCount > 0}
          onClick={() => lifecycleMut.mutate('submit')}
        >
          Submit
        </Button>
        <Button
          disabled={dirtyCount > 0 || sheet?.status !== 'SUBMITTED'}
          onClick={() => lifecycleMut.mutate('verify')}
        >
          Verify
        </Button>
        <Button
          icon={<CloseOutlined />}
          disabled={dirtyCount > 0 || !['VERIFIED', 'SUBMITTED'].includes(String(sheet?.status))}
          onClick={() => lifecycleMut.mutate('close')}
        >
          Close
        </Button>
        <Button disabled={!canEdit} onClick={() => lifecycleMut.mutate('load')}>
          Load expected
        </Button>
        <Dropdown
          menu={{
            items: GROUP_ORDER.map((g) => ({
              key: g,
              label: (
                <Checkbox
                  checked={visibleGroups[g]}
                  onChange={(e) =>
                    setVisibleGroups((prev) => ({ ...prev, [g]: e.target.checked }))
                  }
                >
                  {g}
                </Checkbox>
              ),
            })),
          }}
          trigger={['click']}
        >
          <Button>Columns</Button>
        </Dropdown>
      </Space>
    </div>
  );

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Daily Log Sheet"
          documentNumber={sheet?.sheet_number}
          statusTag={sheet ? <Tag>{sheet.status}</Tag> : undefined}
          subtitle={
            sheet ? (
              <Space size="middle" wrap>
                <Text type="secondary">
                  {resolveProjectDisplayFromMap(projectLabels, sheet.project_id)}
                </Text>
                <Text type="secondary">{sheet.site_reference || '—'}</Text>
                <Text type="secondary">{sheet.operating_date}</Text>
                <Text type="secondary">{sheet.shift_code}</Text>
                <Text type="secondary">Rev {rowVersion}</Text>
              </Space>
            ) : undefined
          }
          primaryActions={
            <Space wrap>
              <Button
                icon={<PrinterOutlined />}
                disabled={!sheet}
                onClick={() => {
                  if (!sheet) return;
                  openDailyLogSheetPrint({
                    sheet,
                    entries: rows,
                    projectLabel: resolveProjectDisplayFromMap(projectLabels, sheet.project_id),
                  });
                }}
              >
                Print
              </Button>
              <Button onClick={() => navigate('/asset/pmv/daily-log-sheets')}>Sheet register</Button>
            </Space>
          }
        />
      }
    >
      {toolbar}

      <Row gutter={12} style={{ marginBottom: 8 }}>
        <Col flex="auto">
          <Space size="large" wrap>
            <Text>
              Completion <Progress percent={completePct} size="small" style={{ width: 120, display: 'inline-flex' }} />
            </Text>
            <Text>
              Exceptions <Tag color={exceptionCount ? 'warning' : 'default'}>{exceptionCount}</Tag>
            </Text>
            <Text>
              Hired <Tag>{hiredCount}</Tag>
            </Text>
            <Text type="secondary">{rows.length} rows</Text>
          </Space>
        </Col>
      </Row>

      <Row gutter={8}>
        <Col xs={24} lg={19}>
          <div
            ref={parentRef}
            onPaste={onPaste}
            style={{
              height: 'calc(100vh - 280px)',
              overflow: 'auto',
              border: '1px solid #d9d9d9',
              background: '#fff',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            }}
          >
            <div style={{ width: totalWidth, position: 'sticky', top: 0, zIndex: 5, display: 'flex', background: '#f0f0f0' }}>
              {visibleCols.map((c) => (
                <div
                  key={c.key}
                  style={{
                    width: c.width,
                    minWidth: c.width,
                    padding: '4px 4px',
                    borderRight: '1px solid #d9d9d9',
                    borderBottom: '1px solid #d9d9d9',
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                  }}
                  title={`${c.group} / ${c.label}`}
                >
                  {c.label}
                </div>
              ))}
            </div>
            <div style={{ height: virtualizer.getTotalSize(), width: totalWidth, position: 'relative' }}>
              {virtualizer.getVirtualItems().map((vRow) => {
                const r = rows[vRow.index];
                const err = validateRowLocal(r);
                return (
                  <div
                    key={r._localKey}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: vRow.size,
                      transform: `translateY(${vRow.start}px)`,
                      display: 'flex',
                    }}
                  >
                    {visibleCols.map((c) => {
                      const focused = focus?.r === vRow.index && focus?.c === c.key;
                      const val = (r as unknown as Record<string, unknown>)[c.key];
                      const editable = canEdit && c.editable;
                      return (
                        <div
                          key={c.key}
                          style={{
                            ...cellStyle(r._dirty, !!err && ['equipment_code', 'opening_meter_value', 'closing_meter_value'].includes(c.key)),
                            width: c.width,
                            minWidth: c.width,
                            outline: focused ? '2px solid #1677ff' : undefined,
                          }}
                          onClick={() => setFocus({ r: vRow.index, c: c.key })}
                        >
                          {!editable || c.key === 'line_no' || c.key === 'daily_log_id' || c.key === 'last_row_result' || c.key === 'last_row_message' ? (
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {c.key === 'daily_log_id' && val ? (
                                <Link to={`/asset/pmv/daily-logs/${val}`}>open</Link>
                              ) : c.type === 'bool' ? (
                                val ? 'Y' : 'N'
                              ) : (
                                String(val ?? '')
                              )}
                            </span>
                          ) : c.type === 'bool' ? (
                            <Checkbox
                              checked={!!val}
                              onChange={(e) => patchCell(vRow.index, c.key, e.target.checked)}
                            />
                          ) : c.type === 'number' ? (
                            <InputNumber
                              size="small"
                              bordered={false}
                              controls={false}
                              value={val as number | null}
                              style={{ width: '100%', fontSize: 12 }}
                              onChange={(v) => patchCell(vRow.index, c.key, v)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  moveFocus(vRow.index, c.key, 'enter');
                                } else if (e.key === 'Tab') {
                                  e.preventDefault();
                                  moveFocus(vRow.index, c.key, e.shiftKey ? 'left' : 'tab');
                                } else if (e.key === 'ArrowDown') moveFocus(vRow.index, c.key, 'down');
                                else if (e.key === 'ArrowUp') moveFocus(vRow.index, c.key, 'up');
                              }}
                            />
                          ) : (
                            <Input
                              size="small"
                              bordered={false}
                              value={String(val ?? '')}
                              style={{ fontSize: 12, padding: 0 }}
                              onChange={(e) => patchCell(vRow.index, c.key, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  moveFocus(vRow.index, c.key, 'enter');
                                } else if (e.key === 'Tab') {
                                  e.preventDefault();
                                  moveFocus(vRow.index, c.key, e.shiftKey ? 'left' : 'tab');
                                }
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
          {errBanner(errMsg(rows))}
        </Col>
        <Col xs={24} lg={5}>
          <div
            style={{
              border: '1px solid #d9d9d9',
              background: '#fff',
              padding: 12,
              height: 'calc(100vh - 280px)',
              overflow: 'auto',
            }}
          >
            <Title level={5} style={{ marginTop: 0 }}>
              Attention
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Sheet is capture envelope only. Certified logs remain authority.
            </Text>
            <div style={{ marginTop: 12 }}>
              {attentionItems.length === 0 ? (
                <Text type="secondary">No open exceptions</Text>
              ) : (
                attentionItems.slice(0, 40).map((a) => (
                  <Alert
                    key={a.key}
                    type={a.severity}
                    message={a.text}
                    style={{ marginBottom: 6, padding: '4px 8px' }}
                    banner
                  />
                ))
              )}
            </div>
          </div>
        </Col>
      </Row>

      <Modal title="Bulk set column" open={bulkOpen} onCancel={() => setBulkOpen(false)} onOk={applyBulk}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <select
            value={bulkField}
            onChange={(e) => setBulkField(e.target.value)}
            style={{ width: '100%', height: 32 }}
          >
            {ALL_COLS.filter((c) => c.editable).map((c) => (
              <option key={c.key} value={c.key}>
                {c.group} / {c.label}
              </option>
            ))}
          </select>
          <Input
            value={String(bulkValue)}
            onChange={(e) => setBulkValue(e.target.value)}
            placeholder="Value for all rows"
          />
        </Space>
      </Modal>

      <Modal
        title="Import / Paste TSV"
        open={importOpen}
        onCancel={() => setImportOpen(false)}
        onOk={() => parseTsv(importText)}
        width={720}
      >
        <Alert
          style={{ marginBottom: 8 }}
          type="info"
          showIcon
          message="Paste Excel/TSV with header row (equipment_code, working_minutes, …). Applied locally until Save."
        />
        <Input.TextArea
          rows={12}
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
          placeholder="equipment_code&#9;working_minutes&#9;idle_minutes"
        />
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.tsv,.txt"
          style={{ display: 'none' }}
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            setImportText(await f.text());
          }}
        />
        <Button style={{ marginTop: 8 }} onClick={() => fileRef.current?.click()}>
          Load file
        </Button>
      </Modal>
    </WorkspaceLayout>
  );
};

function errMsg(rows: SheetRow[]): string | null {
  const first = rows.find((r) => validateRowLocal(r));
  return first ? validateRowLocal(first) : null;
}

function errBanner(msg: string | null) {
  if (!msg) return null;
  return (
    <Alert style={{ marginTop: 8 }} type="warning" showIcon message={`Inline validation: ${msg}`} />
  );
}

export default PmvDailyLogSheetPage;
